/**
 * ApiKeyHeaderAuthPage — settings form + test-request panel + curl example.
 *
 * Visual system: GREEN accent only (green-50/600/700). No emerald/sky/blue/
 * orange/purple/dark. Zinc borders, 6-8px rounding.
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useApiKeyHeaderAuthList,
	useApiKeyHeaderAuthCreate,
	useApiKeyHeaderAuthDelete,
} from "./hooks";
import { testRequest, type TestRequestResult } from "./api";

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const BTN =
	"inline-flex items-center gap-1.5 rounded-[6px] border border-green-600 bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50";
const LABEL =
	"text-xs font-medium uppercase tracking-wider text-zinc-600";

const HEADER_RE = /^[A-Za-z][A-Za-z0-9-]{0,63}$/;
const PREFIX_RE = /^[A-Za-z0-9_-]{0,16}$/;

function escSingleQuote(s: string): string {
	return s.replace(/'/g, "'\\''");
}

export function ApiKeyHeaderAuthPage() {
	const listQuery = useApiKeyHeaderAuthList();
	const createMut = useApiKeyHeaderAuthCreate();
	const deleteMut = useApiKeyHeaderAuthDelete();

	const [headerName, setHeaderName] = useState("X-API-Key");
	const [prefix, setPrefix] = useState("tk_");
	const [saved, setSaved] = useState<{
		headerName: string;
		prefix: string;
	} | null>(null);

	const [testKey, setTestKey] = useState("");
	const [testing, setTesting] = useState(false);
	const [testResult, setTestResult] = useState<TestRequestResult | null>(null);
	const [testError, setTestError] = useState<string | null>(null);

	const headerNameValid = HEADER_RE.test(headerName.trim());
	const prefixValid = PREFIX_RE.test(prefix);
	const settingsValid = headerNameValid && prefixValid;

	const handleSaveSettings = (e: FormEvent) => {
		e.preventDefault();
		if (!settingsValid) return;
		setSaved({ headerName: headerName.trim(), prefix });
	};

	const handleTest = async (e: FormEvent) => {
		e.preventDefault();
		if (!testKey.trim()) return;
		setTesting(true);
		setTestError(null);
		setTestResult(null);
		try {
			const hn = saved?.headerName ?? headerName.trim();
			const result = await testRequest(hn, testKey.trim());
			setTestResult(result);
		} catch (err) {
			setTestError(err instanceof Error ? err.message : String(err));
		} finally {
			setTesting(false);
		}
	};

	const curlExample = useMemo(() => {
		const hn = (saved?.headerName ?? headerName.trim()) || "X-API-Key";
		const px = saved?.prefix ?? prefix;
		const sampleKey = `${px}your_secret_key_here`;
		return [
			"curl -sS \\",
			`  -H '${escSingleQuote(hn)}: ${escSingleQuote(sampleKey)}' \\`,
			"  -H 'x-tenant-id: <your-tenant-id>' \\",
			"  https://api.example.com/api/v1/api-key-header-auth/keys",
		].join("\n");
	}, [headerName, prefix, saved]);

	const items = listQuery.data?.items ?? [];

	return (
		<main
			data-testid="api-key-header-auth-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					auth
				</p>
				<h1 className="text-3xl font-semibold text-green-700">
					Header API-key auth
				</h1>
				<p className="max-w-2xl text-sm text-zinc-600">
					Configure the HTTP header and key prefix your services send,
					then verify the integration with a live test request.
				</p>
			</header>

			<section className={CARD} aria-label="Header settings">
				<h2 className="text-base font-semibold text-zinc-900">
					Header settings
				</h2>
				<p className="mt-1 text-xs text-zinc-600">
					Applies to keys sent on inbound API calls.
				</p>
				<form
					onSubmit={handleSaveSettings}
					className="mt-4 flex flex-col gap-4"
					data-testid="api-key-header-auth-settings-form"
				>
					<div className="flex flex-col gap-1.5">
						<label className={LABEL} htmlFor="header-name">
							Header name
						</label>
						<input
							id="header-name"
							type="text"
							className={INPUT}
							placeholder="X-API-Key"
							value={headerName}
							onChange={(e) => setHeaderName(e.currentTarget.value)}
							data-testid="api-key-header-auth-header-name"
							autoComplete="off"
							spellCheck={false}
						/>
						{!headerNameValid ? (
							<p className="text-xs text-red-600" role="alert">
								Letters, digits, hyphens. Must start with a letter.
							</p>
						) : null}
					</div>
					<div className="flex flex-col gap-1.5">
						<label className={LABEL} htmlFor="prefix">
							Key prefix
						</label>
						<input
							id="prefix"
							type="text"
							className={INPUT}
							placeholder="tk_"
							value={prefix}
							onChange={(e) => setPrefix(e.currentTarget.value)}
							data-testid="api-key-header-auth-prefix"
							autoComplete="off"
							spellCheck={false}
						/>
						{!prefixValid ? (
							<p className="text-xs text-red-600" role="alert">
								Up to 16 alphanumerics, `_`, or `-`.
							</p>
						) : null}
					</div>
					<div className="flex items-center gap-3">
						<button
							type="submit"
							className={BTN}
							disabled={!settingsValid}
							data-testid="api-key-header-auth-save"
						>
							Save settings
						</button>
						{saved ? (
							<span
								className="rounded-[6px] border border-green-600 bg-green-50 px-2 py-1 text-xs font-medium text-green-700"
								data-testid="api-key-header-auth-saved"
							>
								Saved: {saved.headerName}
							</span>
						) : null}
					</div>
				</form>
			</section>

			<section className={CARD} aria-label="Test request">
				<h2 className="text-base font-semibold text-zinc-900">
					Send a test request
				</h2>
				<p className="mt-1 text-xs text-zinc-600">
					Paste a secret you minted and verify the server accepts it.
				</p>
				<form
					onSubmit={handleTest}
					className="mt-4 flex flex-col gap-3"
					data-testid="api-key-header-auth-test-form"
				>
					<input
						type="text"
						className={INPUT}
						placeholder="Secret key value"
						value={testKey}
						onChange={(e) => setTestKey(e.currentTarget.value)}
						disabled={testing}
						data-testid="api-key-header-auth-test-key"
						autoComplete="off"
						spellCheck={false}
					/>
					<div>
						<button
							type="submit"
							className={BTN}
							disabled={testing || !testKey.trim()}
							data-testid="api-key-header-auth-send-test"
						>
							{testing ? "Sending..." : "Send test"}
						</button>
					</div>
				</form>
				{testError ? (
					<p
						className="mt-3 text-xs text-red-600"
						role="alert"
						data-testid="api-key-header-auth-test-error"
					>
						{testError}
					</p>
				) : null}
				{testResult ? (
					<div
						className="mt-3 rounded-[6px] border border-zinc-200 bg-zinc-50 p-3"
						data-testid="api-key-header-auth-test-result"
					>
						<p className="text-xs font-medium text-zinc-700">
							HTTP{" "}
							<span
								className={
									testResult.ok
										? "text-green-700"
										: "text-red-600"
								}
								data-testid="api-key-header-auth-test-status"
							>
								{testResult.status}
							</span>{" "}
							— {testResult.ok ? "accepted" : "rejected"}
						</p>
						<pre
							className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap break-words font-mono text-xs text-zinc-800"
							data-testid="api-key-header-auth-test-body"
						>
							{testResult.body || "(empty body)"}
						</pre>
					</div>
				) : null}
			</section>

			<section className={CARD} aria-label="Example curl">
				<h2 className="text-base font-semibold text-zinc-900">
					Example request
				</h2>
				<p className="mt-1 text-xs text-zinc-600">
					Copy and adapt with your tenant id + secret.
				</p>
				<pre
					className="mt-3 overflow-auto rounded-[6px] border border-zinc-200 bg-zinc-50 p-3 font-mono text-xs text-zinc-800"
					data-testid="api-key-header-auth-curl"
				>
					{curlExample}
				</pre>
			</section>

			<section className={CARD} aria-label="Existing keys">
				<h2 className="text-base font-semibold text-zinc-900">
					Existing keys
				</h2>
				{listQuery.isLoading ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="api-key-header-auth-loading"
					>
						Loading...
					</p>
				) : listQuery.error ? (
					<p
						className="mt-3 text-sm text-red-600"
						role="alert"
						data-testid="api-key-header-auth-list-error"
					>
						{listQuery.error.message}
					</p>
				) : items.length === 0 ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="api-key-header-auth-empty"
					>
						No keys minted yet.
					</p>
				) : (
					<ul
						className="mt-3 divide-y divide-zinc-200"
						data-testid="api-key-header-auth-list"
					>
						{items.map((row) => (
							<li
								key={row.id}
								className="flex items-center justify-between py-2 text-sm"
								data-testid={`api-key-header-auth-row-${row.id}`}
							>
								<span className="font-mono text-xs text-zinc-600">
									{row.id}
								</span>
								<button
									type="button"
									className={BTN_GHOST}
									onClick={() => deleteMut.mutate(row.id)}
									disabled={deleteMut.isPending}
									data-testid={`api-key-header-auth-delete-${row.id}`}
								>
									Revoke
								</button>
							</li>
						))}
					</ul>
				)}
			</section>

			{createMut.error ? (
				<p
					className="text-xs text-red-600"
					role="alert"
					data-testid="api-key-header-auth-create-error"
				>
					{createMut.error.message}
				</p>
			) : null}
		</main>
	);
}

export default ApiKeyHeaderAuthPage;
